import {
	Collapse,
	createStyles,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	Theme,
	Tooltip,
	Typography,
	withStyles,
	WithStyles,
} from '@material-ui/core'
import { blue, blueGrey, grey } from '@material-ui/core/colors'
import moment from 'moment'
import React from 'react'
import { NavLink } from 'react-router-dom'

import { Sort } from '../services/sortingService'

export interface TableHeader {
	label: string
	prop: string
}

export interface TitleItem {
	label: string
	url: string
}

export interface TableItem {
	title: TitleItem
	content: TableContent[]
}

export interface TableContent {
	isDate: boolean
	value: string | Date
  label?: string
}

export interface HeaderItem {
	label: string
	prop: string
}

interface TableProps extends WithStyles<typeof styles> {
	loading: boolean
	headers: TableHeader[]
	rowItems: any[]
	onSortClick: (direction: string, orderBy: string) => void
	sortDirection: Sort
	orderBy: string
	paginationFooter?: boolean
}

interface TableWrapperState {
	rowsPerPage: number
	page: number
}

export class TableWrapper extends React.Component<TableProps, TableWrapperState> {
	state: TableWrapperState = {
		rowsPerPage: 10,
		page: 0,
	}

	handleChangePage = (event: any, page: number) => {
		this.setState({ page })
	}

	handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		this.setState({ rowsPerPage: +event.target.value })
	}

	handleSortClick = (prop: string) => (e: React.MouseEvent<HTMLInputElement>) => {
		const { onSortClick, sortDirection } = this.props

		onSortClick(sortDirection, prop)
	}

	render() {
		const { classes, headers, loading, sortDirection, orderBy, rowItems, paginationFooter } = this.props
		const { page, rowsPerPage } = this.state

		return (
			<Paper className={classes.root} square={true} elevation={2} data-auto="main-table-template-wrapper">
				<Collapse in={!loading} timeout={{ enter: 700 }}>
					{!rowItems.length || rowItems.length === 0 ? (
						<Typography variant={'h6'} component={'h3'} className={classes.emptyTable} color="secondary">
							No entries found.
						</Typography>
					) : (
						<Table className={classes.table} data-auto="main-table">
							<TableHead className={classes.tableHeader} data-auto="main-table-header">
								<TableRow className={classes.tableRow}>
									{headers &&
										headers.map((header: HeaderItem, index: number) => {
											const { label, prop } = header

											return (
												header && (
													<TableCell
														key={index}
														className={classes.headCellItem}
														data-auto={`table-header-row-item-${prop}`}
													>
														<Tooltip title="Sort" placement="right" enterDelay={300}>
															<TableSortLabel
																active={prop.toLowerCase() === orderBy.toLowerCase()}
																direction={sortDirection}
																onClick={this.handleSortClick(prop)}
																style={{ color: grey[50] }}
															>
																<Typography variant="button" className={classes.typographyHeader}>
																	{label}
																</Typography>
															</TableSortLabel>
														</Tooltip>
													</TableCell>
												)
											)
										})}
								</TableRow>
							</TableHead>
							<TableBody data-auto="main-table-body">
								{rowItems
									.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
									.map((item: TableItem, index: number) => {
										const { title, content } = item

										return (
											<TableRow key={index} className={classes.row} data-auto="main-table-row">
												{title && (
													<TableCell
														component="th"
														scope="row"
														className={classes.mainColumnItem}
														data-auto={`row-${index}-title-item`}
													>
														<NavLink to={title.url} className={classes.linkBtn} data-auto="row-item-title-link">
															<Typography variant="button" className={classes.link} data-auto="row-item-title-label">
																{title.label}
															</Typography>
														</NavLink>
													</TableCell>
												)}
												{content &&
													content.map((contentItem: any, i: number) => (
														<TableCell key={i} data-auto={`row-${index}-content-item`}>
															<Typography variant="body1">
																{contentItem.isDate && contentItem.value ? (
																	<time dateTime={moment(contentItem.value).format('MM/DD/YYYY')}>
																		{moment(contentItem.value).format('MM/DD/YYYY')}
																	</time>
																) : (
																	contentItem.value
																)}
															</Typography>
														</TableCell>
													))}
											</TableRow>
										)
									})}
							</TableBody>
							{paginationFooter && (
								<TableFooter data-auto="main-table-footer">
									<TableRow className={classes.footerRow}>
										<TableCell colSpan={headers.length}>
											<TablePagination
												classes={{
													toolbar: classes.toolbar,
												}}
												rowsPerPageOptions={[10, 20, 30]}
												component="div"
												count={rowItems.length}
												rowsPerPage={rowsPerPage}
												page={page}
												backIconButtonProps={{
													'aria-label': 'previous-page',
												}}
												nextIconButtonProps={{
													'aria-label': 'next-page',
												}}
												SelectProps={{
													'aria-label': 'items-per-page-select',
													inputProps: {
														'data-auto': 'items-per-page-select',
													},
												}}
												onChangePage={this.handleChangePage}
												onChangeRowsPerPage={this.handleChangeRowsPerPage}
												data-auto="main-table-footer-pagination"
											/>
										</TableCell>
									</TableRow>
								</TableFooter>
							)}
						</Table>
					)}
				</Collapse>
			</Paper>
		)
	}
}

const styles = (theme: Theme) =>
	createStyles({
		toolbar: {
			[theme.breakpoints.down('sm')]: {
				display: 'inline-flex',
			},
		},
		root: {
			flexGrow: 1,
			overflowX: 'scroll',
		},
		table: {},
		tableHeader: {
			backgroundColor: blue[900],
		},
		tableRow: {
			colSpan: 2,
		},
		headCellItem: {
			textOverflow: 'ellipsis',
		},
		mainColumnItem: {
			textOverflow: 'ellipsis',
		},
		row: {
			'&:nth-of-type(odd)': {
				backgroundColor: grey[200],
			},
			'&:hover': {
				backgroundColor: blueGrey[100],
			},
		},
		footerRow: {
			padding: `${theme.spacing.unit}px`,
		},
		linkBtn: {
			textDecoration: 'none',
		},
		link: {
			transition: 'transform 200ms, color 200ms, letter-spacing 500ms',
			textTransform: 'none',
			'&:hover': {
				color: theme.palette.primary[900],
				[theme.breakpoints.up('md')]: {
					transform: 'scale(1, 1.25)',
					letterSpacing: '0.1em',
				},
			},
		},
		typographyHeader: {
			color: grey[50],
		},
		emptyTable: {
			padding: `${theme.spacing.unit * 2}px`,
			textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
			fontWeight: 500,
			[theme.breakpoints.down('sm')]: {
				padding: `${theme.spacing.unit}px 0`,
			},
		},
	})

export default withStyles(styles)(TableWrapper)
